import { useState } from 'react'
import { router } from '@inertiajs/react'
import { ArrowLeftIcon, ThumbsUpIcon, MessageSquareIcon, SendIcon, UserCircle2Icon, RefreshCwIcon, UserPlusIcon, ExternalLinkIcon, SparklesIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { showSuccess, showError } from '@/lib/toast-helpers'

interface Comment {
  id: number
  comment_id: string
  author_name: string
  author_channel_url: string | null
  author_profile_image_url: string | null
  text_display: string
  text_original: string
  like_count: number
  published_at: string
  can_reply: boolean
  has_lead: boolean
  lead_id: number | null
  replies: Comment[]
}

interface Video {
  id: number
  video_id: string
  title: string
  description: string | null
  thumbnail_url: string
  view_count: number
  like_count: number
  comment_count: number
  published_at: string
  duration: string | null
  comment_summary: string | null
  youtube_connection: {
    id: number
    channel_title: string
  }
}

interface VideoShowProps {
  video: Video
  comments: Comment[]
}

export default function VideoShow({ video, comments }: VideoShowProps) {
  const [replyingTo, setReplyingTo] = useState<number | null>(null)
  const [replyText, setReplyText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [createLeadDialogOpen, setCreateLeadDialogOpen] = useState(false)
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null)
  const [selectedLeadType, setSelectedLeadType] = useState<'shop' | 'driver'>('shop')
  const [commentSummary, setCommentSummary] = useState<string | null>(video.comment_summary)
  const [summaryLoading, setSummaryLoading] = useState(false)

  const handleBack = () => {
    router.visit('/youtube/videos')
  }

  const renderMarkdown = (text: string) => {
    // Simple markdown to HTML converter for basic formatting
    return text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') // Bold
      .replace(/\*(.+?)\*/g, '<em>$1</em>') // Italic
      .replace(/\n/g, '<br/>') // Line breaks
  }

  const handleGenerateSummary = async () => {
    setSummaryLoading(true)
    try {
      const token = localStorage.getItem('auth_token')
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        'Accept': 'application/json'
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(`/youtube/videos/${video.id}/summarize_comments`, {
        method: 'POST',
        headers
      })

      const data = await response.json()

      if (data.success) {
        setCommentSummary(data.summary)
        showSuccess('Summary generated successfully')
      } else {
        showError(data.errors?.join(', ') || 'Failed to generate summary')
      }
    } catch (error) {
      showError('Failed to generate summary')
    } finally {
      setSummaryLoading(false)
    }
  }

  const handleReply = async (commentId: string) => {
    if (!replyText.trim()) return

    setSubmitting(true)
    try {
      const token = localStorage.getItem('auth_token')
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        'Accept': 'application/json'
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/youtube/comments', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          video_id: video.video_id,
          comment_text: replyText,
          parent_comment_id: commentId
        })
      })

      const data = await response.json()

      if (data.success) {
        showSuccess('Reply posted successfully')
        setReplyText('')
        setReplyingTo(null)
        router.reload({ only: ['comments'] })
      } else {
        showError(data.errors?.join(', ') || 'Failed to post reply')
      }
    } catch (error) {
      showError('Failed to post reply')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCreateLead = (comment: Comment) => {
    setSelectedComment(comment)
    setSelectedLeadType('shop')
    setCreateLeadDialogOpen(true)
  }

  const confirmCreateLead = async () => {
    if (!selectedComment) return

    try {
      const token = localStorage.getItem('auth_token')
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        'Accept': 'application/json'
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(`/youtube/comments/${selectedComment.id}/create_lead`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ lead_type: selectedLeadType })
      })

      if (!response.ok) {
        const text = await response.text()
        console.error('Create lead failed:', response.status, text)
        showError(`Failed to create lead (${response.status})`)
        return
      }

      const data = await response.json()

      if (data.success) {
        showSuccess('Lead created successfully')
        setCreateLeadDialogOpen(false)
        router.reload({ only: ['comments'] })
      } else {
        showError(data.errors?.join(', ') || 'Failed to create lead')
      }
    } catch (error) {
      console.error('Create lead error:', error)
      showError('Failed to create lead')
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMs = now.getTime() - date.getTime()
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

    if (diffInDays === 0) {
      return 'Today'
    } else if (diffInDays === 1) {
      return 'Yesterday'
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7)
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`
    } else if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30)
      return `${months} month${months > 1 ? 's' : ''} ago`
    } else {
      const years = Math.floor(diffInDays / 365)
      return `${years} year${years > 1 ? 's' : ''} ago`
    }
  }

  const renderComment = (comment: Comment, isReply = false) => (
    <div key={comment.id} className={`${isReply ? 'ml-12 mt-3' : 'mb-4'}`} data-testid={`comment-${comment.id}`}>
      <div className="flex gap-3">
        <Avatar className="size-10 flex-shrink-0">
          <AvatarImage src={comment.author_profile_image_url || undefined} />
          <AvatarFallback>
            <UserCircle2Icon className="size-6" />
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">{comment.author_name}</span>
            <span className="text-xs text-muted-foreground">{formatDate(comment.published_at)}</span>
            {comment.has_lead && (
              <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-300">
                Lead Created
              </Badge>
            )}
          </div>

          <div
            className="text-sm whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: comment.text_display }}
          />

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <ThumbsUpIcon className="size-3" />
              <span>{comment.like_count}</span>
            </div>

            {!isReply && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto py-1 px-2 text-xs"
                onClick={() => setReplyingTo(comment.id)}
                data-testid={`reply-button-${comment.id}`}
              >
                Reply
              </Button>
            )}

            {!comment.has_lead && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto py-1 px-2 text-xs text-blue-600 hover:text-blue-700"
                onClick={() => handleCreateLead(comment)}
                data-testid={`create-lead-button-${comment.id}`}
              >
                <UserPlusIcon className="size-3 mr-1" />
                Create Lead
              </Button>
            )}

            {comment.has_lead && comment.lead_id && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto py-1 px-2 text-xs text-green-600 hover:text-green-700"
                onClick={() => router.visit(`/leads/${comment.lead_id}`)}
                data-testid={`view-lead-button-${comment.id}`}
              >
                <ExternalLinkIcon className="size-3 mr-1" />
                View Lead
              </Button>
            )}
          </div>

          {replyingTo === comment.id && (
            <div className="mt-3 space-y-2">
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write a reply..."
                className="min-h-[80px]"
                data-testid="reply-textarea"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handleReply(comment.comment_id)}
                  disabled={submitting || !replyText.trim()}
                  data-testid="submit-reply-button"
                >
                  <SendIcon className="size-4 mr-1" />
                  Reply
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setReplyingTo(null)
                    setReplyText('')
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Replies */}
          {comment.replies && comment.replies.length > 0 && (
            <div className="mt-3 space-y-3">
              {comment.replies.map((reply) => renderComment(reply, true))}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
          <div className="px-4 lg:px-6">
            {/* Back Button */}
            <Button
              variant="ghost"
              className="mb-4"
              onClick={handleBack}
              data-testid="back-button"
            >
              <ArrowLeftIcon className="size-4 mr-2" />
              Back to Videos
            </Button>

            <div className="max-w-4xl mx-auto space-y-4">
                {/* Video Details */}
                <Card>
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <img
                        src={video.thumbnail_url}
                        alt={video.title}
                        className="w-32 h-20 object-cover rounded"
                      />
                      <div className="flex-1">
                        <CardTitle className="mb-2">{video.title}</CardTitle>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{video.view_count.toLocaleString()} views</span>
                          <span>•</span>
                          <span>{formatDate(video.published_at)}</span>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {video.description && (
                      <div className="text-sm whitespace-pre-wrap text-muted-foreground">
                        {video.description}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* AI Comment Summary */}
                {comments.length > 0 && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          <SparklesIcon className="size-5 text-purple-500" />
                          AI Summary
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {!commentSummary ? (
                        <div>
                          <p className="text-sm text-muted-foreground mb-3">
                            Get AI-powered insights and key points from the comments
                          </p>
                          <Button
                            onClick={handleGenerateSummary}
                            disabled={summaryLoading}
                            className="w-full"
                            size="sm"
                            data-testid="generate-summary-button"
                          >
                            {summaryLoading ? (
                              <>
                                <RefreshCwIcon className="size-4 mr-2 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <SparklesIcon className="size-4 mr-2" />
                                Generate Summary
                              </>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <div
                            className="text-sm mb-3"
                            dangerouslySetInnerHTML={{ __html: renderMarkdown(commentSummary) }}
                          />
                          <Button
                            onClick={handleGenerateSummary}
                            disabled={summaryLoading}
                            variant="outline"
                            size="sm"
                            className="w-full"
                          >
                            <RefreshCwIcon className="size-4 mr-2" />
                            Regenerate
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Statistics */}
                <Card>
                  <CardHeader>
                    <CardTitle>Statistics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Views</span>
                      <span className="font-semibold">{video.view_count.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Likes</span>
                      <span className="font-semibold">{video.like_count.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Comments</span>
                      <span className="font-semibold">{video.comment_count.toLocaleString()}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Comments Section */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <MessageSquareIcon className="size-5" />
                        {comments.length} Comments
                      </CardTitle>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => router.reload({ only: ['comments'] })}
                      >
                        <RefreshCwIcon className="size-4 mr-1" />
                        Refresh
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {comments.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No comments yet
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {comments.map((comment) => renderComment(comment))}
                      </div>
                    )}
                  </CardContent>
                </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Create Lead Confirmation Dialog */}
      <AlertDialog open={createLeadDialogOpen} onOpenChange={setCreateLeadDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Create Lead from Commenter?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {selectedComment && (
                  <div className="space-y-3 mt-4">
                    <div>
                      <div className="font-semibold text-sm text-foreground mb-1">Commenter</div>
                      <div className="text-sm">{selectedComment.author_name}</div>
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-foreground mb-1">Comment</div>
                      <div className="text-sm line-clamp-3">{selectedComment.text_original}</div>
                    </div>
                    {selectedComment.author_channel_url && (
                      <div>
                        <div className="font-semibold text-sm text-foreground mb-1">YouTube Channel</div>
                        <a
                          href={selectedComment.author_channel_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline"
                        >
                          View Channel
                        </a>
                      </div>
                    )}
                    <div className="mt-4 space-y-3">
                      <div className="font-semibold text-sm text-foreground">Lead Type</div>
                      <RadioGroup
                        value={selectedLeadType}
                        onValueChange={(value) => setSelectedLeadType(value as 'shop' | 'driver')}
                        data-testid="lead-type-radio-group"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="shop" id="shop" data-testid="shop-radio" />
                          <Label htmlFor="shop" className="cursor-pointer">
                            <div className="font-medium">Modern Shop</div>
                            <div className="text-xs text-muted-foreground">For truck repair and maintenance inquiries</div>
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="driver" id="driver" data-testid="driver-radio" />
                          <Label htmlFor="driver" className="cursor-pointer">
                            <div className="font-medium">Modern Nomad</div>
                            <div className="text-xs text-muted-foreground">For driver recruitment and hiring</div>
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCreateLead} className="bg-blue-600 hover:bg-blue-700">
              Create Lead
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
